import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import {
  Box, Card, CardContent, CardMedia, Typography, IconButton,
  Button, Tooltip, Chip, CircularProgress,
} from '@mui/material';
import {
  ExploreOutlined, SquareFootOutlined, PersonOutlineOutlined,
} from '@mui/icons-material';
import {
  Favorite, Share, Call, Chat, LocationOn, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CustomSearchBar from './CustomSearch';
import BottomNavbar from './CustomBottomNav';
import { AuthContext } from './../../AuthContext/AuthContext';
import { BASE_URL } from './../../Api/ApiUrls';
import CopySuccessAlert from '../Alertpage/CopyAlert/CopySuccessAlert';
import CopyFailureAlert from '../Alertpage/CopyAlert/CopyFailureAlert';
import WishlistRemovedAlert from '../Alertpage/CopyAlert/WishlistRemovedAlert';
import WishlistNotRemovedAlert from '../Alertpage/CopyAlert/WishlistNotRemovedAlert';
import buildingImage from './../../../Images/house.jpeg';
import { NODE_API } from '../Buy Module/NodeApi';

const PAGE_SIZE = 20;

// ─── Skeleton card (horizontal, mirrors PropertyCard) ─────────────────────────
const SkeletonCard = () => (
  <Box sx={{
    mb: 1.2, mx: 2, borderRadius: 3, overflow: 'hidden', boxShadow: 2,
    backgroundColor: '#fff',
    animation: 'skeletonPulse 1.5s ease-in-out infinite',
    '@keyframes skeletonPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
  }}>
    <Box sx={{ display: 'flex', height: 140 }}>
      <Box sx={{ width: '40%', backgroundColor: '#ddd' }} />
      <Box sx={{ width: '60%', p: 1.5 }}>
        <Box sx={{ height: 14, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '65%' }} />
        <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '45%' }} />
        <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1.5, width: '55%' }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
        </Box>
      </Box>
    </Box>
  </Box>
);

// ─── Chip skeleton ──────────────────────────────────────────────────────────────
// ✅ matched to PropertyCard.js
const ChipRowSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 0.6, px: 0, pb: 1, overflowX: 'hidden' }}>
    {[80, 60, 100, 70, 90].map((w, i) => (
      <Box
        key={i}
        sx={{
          width: w,
          height: 24,
          borderRadius: '20px',
          flexShrink: 0,
          backgroundColor: '#d0d0d0',
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
          '@keyframes skeletonPulse': {
            '0%,100%': { opacity: 1 },
            '50%': { opacity: 0.45 },
          },
        }}
      />
    ))}
  </Box>
);

// ─── Scroll sentinel ────────────────────────────────────────────────────────────
const ScrollSentinel = ({ onVisible, hasMore, loading }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) onVisible(); },
      { threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible, hasMore, loading]);

  return (
    <Box ref={ref} sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {loading && <CircularProgress size={28} sx={{ color: '#8B7355' }} />}
      {!loading && !hasMore && (
        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
          ✓ All saved properties loaded
        </Typography>
      )}
    </Box>
  );
};

// ─── Parse raw API row → card shape ────────────────────────────────────────────
const parseProperty = (item) => {
  const parseCoord = (coord) => {
    if (!coord) return null;
    return parseFloat(String(coord).replace(/"/g, '').replace(':', '.'));
  };

  let images = [buildingImage];
  if (item.property_images?.length > 0) {
    images = item.property_images.map(img => {
      if (typeof img === 'string') return img.startsWith('http') ? img : `${BASE_URL}${img}`;
      if (img.image) return img.image.startsWith('http') ? img.image : `${BASE_URL}/${img.image.replace(/^\/+/, '')}`;
      if (img.url)   return img.url.startsWith('http')   ? img.url   : `${BASE_URL}${img.url}`;
      if (img.path)  return img.path.startsWith('http')  ? img.path  : `${BASE_URL}${img.path}`;
      return buildingImage;
    });
  } else if (item.images?.length > 0) {
    images = item.images.map(img =>
      typeof img === 'string' && img.startsWith('http') ? img : `${BASE_URL}${img}`
    );
  }

  return {
    id:            String(item.property_id),
    cart_id:       item.cart_id,
    saved_at:      item.saved_at,
    title:         item.category_name || item.property_type || 'Property',
    property_name: item.property_name,
    location:      item.location || 'Not specified',
    date:          item.created_at?.split('T')[0],
    facing:        item.facing?.replace(/"/g, '') || 'N/A',
    price:         Number(item.price) || 0,
    category_id:   item.category_id,
    category_name: item.category_name || '',
    images,
    site_area:     item.site_area,
    area: item.site_area
      ? `${item.site_area} ${['ft','m','cm','in'].includes(item.units) ? `Sq ${item.units}` : item.units || 'Sq ft'}`
      : 0,
    max_acres:  item.max_acres ? `${item.max_acres} ${item.units || 'acres'}` : 'N/A',
    posted_by:  item.posted_by?.replace(/"/g, '') || 'Agent',
    mobile_no:  item.mobile_no,
    length:     item.length,
    width:      item.width,
    units:      item.units || '',
    lat:        parseCoord(item.lat),
    long:       parseCoord(item.long),
    owner:      item.user_id,
    propertyData: item,
    property_images: item.property_images,
  };
};

// ─── Debounce hook ──────────────────────────────────────────────────────────────
const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// ─── Generic paginated fetch hook ──────────────────────────────────────────────
const usePaginatedFetch = (getUrl, deps, enabled = true) => {
  const [items,          setItems]          = useState([]);
  const [cursor,         setCursor]         = useState(null);
  const [hasMore,        setHasMore]        = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching,     setIsFetching]     = useState(false);
  const fetchingRef = useRef(false);
  const abortRef    = useRef(null);

  const setFetching = (val) => { fetchingRef.current = val; setIsFetching(val); };

  useEffect(() => {
    if (!enabled) { setInitialLoading(false); return; }

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setItems([]);
    setCursor(null);
    setHasMore(false);
    setInitialLoading(true);
    setFetching(true);

    fetch(getUrl(null), { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(json => {
        setItems((json.data || []).map(parseProperty));
        setCursor(json.next_cursor ?? null);
        setHasMore(json.has_more ?? false);
      })
      .catch(err => { if (err.name !== 'AbortError') console.error('fetch error:', err); })
      .finally(() => { setInitialLoading(false); setFetching(false); });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore || !cursor) return;

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);

    fetch(getUrl(cursor), { signal: ctrl.signal })
      .then(r => r.json())
      .then(json => {
        setItems(prev => [...prev, ...(json.data || []).map(parseProperty)]);
        setCursor(json.next_cursor ?? null);
        setHasMore(json.has_more ?? false);
      })
      .catch(err => { if (err.name !== 'AbortError') console.error('loadMore error:', err); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, cursor, ...deps]);

  const removeItem = useCallback((propertyId) => {
    setItems(prev => prev.filter(p => p.id !== String(propertyId)));
  }, []);

  return { items, hasMore, loading, initialLoading, isFetching, fetchingRef, loadMore, removeItem };
};

// ─── Hook: saved lease properties ──────────────────────────────────────────────
const useSavedLeaseProperties = ({ userId, searchQuery, priceFilter, categoryId, enabled }) => {
  const getUrl = useCallback((cursorVal) => {
    const p = new URLSearchParams({ limit: PAGE_SIZE });
    if (cursorVal)                p.set('cursor',      cursorVal);
    if (searchQuery?.trim())      p.set('search',      searchQuery.trim());
    if (priceFilter?.min != null) p.set('min_price',   priceFilter.min);
    if (priceFilter?.max != null) p.set('max_price',   priceFilter.max);
    if (categoryId != null)       p.set('category_id', categoryId);
    return `${NODE_API}/api/lease/saved/${userId}?${p}`;
  }, [userId, searchQuery, priceFilter, categoryId]);

  return usePaginatedFetch(
    getUrl,
    [userId, searchQuery, JSON.stringify(priceFilter), categoryId],
    enabled && !!userId
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────────
const LeaseSaves = () => {
  const navigate   = useNavigate();
  const { userId } = useContext(AuthContext);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [searchQuery,       setSearchQuery]       = useState('');
  const [priceFilter,       setPriceFilter]       = useState(null);
  const [selectedCategory,  setSelectedCategory]  = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [categories,        setCategories]        = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [locationLoading,   setLocationLoading]   = useState({});

  // ── Alert state ──────────────────────────────────────────────────────────────
  const [showSuccess,    setShowSuccess]    = useState(false);
  const [showFailure,    setShowFailure]    = useState(false);
  const [showRemoved,    setShowRemoved]    = useState(false);
  const [showNotRemoved, setShowNotRemoved] = useState(false);
  const [alertMessage,   setAlertMessage]   = useState('');

  const debouncedSearch = useDebounce(searchQuery, 400);
  const isTyping = searchQuery !== debouncedSearch;

  // ── Data hook ────────────────────────────────────────────────────────────────
  const {
    items:          properties,
    hasMore,
    loading:        listLoading,
    initialLoading,
    isFetching,
    fetchingRef,
    loadMore,
    removeItem,
  } = useSavedLeaseProperties({
    userId,
    searchQuery:  debouncedSearch,
    priceFilter,
    categoryId:   selectedCategory?.category_id ?? null,
    enabled:      !!userId,
  });

  const showSkeleton = isTyping || isFetching || fetchingRef?.current;

  // ── Fetch lease categories ────────────────────────────────────────────────────
  useEffect(() => {
    setCategoriesLoading(true);
    fetch(`${NODE_API}/api/saved-lease/categories/`)
      .then(r => r.json())
      .then(data => { setCategories(data); setCategoriesLoading(false); })
      .catch(() => setCategoriesLoading(false));
  }, []);

  // ── Sync image indexes ────────────────────────────────────────────────────────
  useEffect(() => {
    setCurrentImageIndex(prev => {
      const next = { ...prev };
      properties.forEach(p => { if (!(p.id in next)) next[p.id] = 0; });
      return next;
    });
  }, [properties]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const findProperty = useCallback(
    (propertyId) => properties.find(p => p.id === String(propertyId)),
    [properties]
  );

  const handleRemove = useCallback(async (property) => {
    try {
      await axios.delete(`${BASE_URL}/user-cart/${property.cart_id}/`);
      removeItem(property.id);
      setAlertMessage('Property removed from wishlist.');
      setShowRemoved(true);
    } catch {
      setAlertMessage('Failed to remove property from wishlist.');
      setShowNotRemoved(true);
    }
  }, [removeItem]);

  const handleCallClick = useCallback(async (e, property) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(property.mobile_no);
      setAlertMessage(`Copied: ${property.mobile_no}`);
      setShowSuccess(true); setShowFailure(false);
    } catch {
      setAlertMessage('Failed to copy number');
      setShowFailure(true); setShowSuccess(false);
    }
  }, []);

  const handleMessageClick = useCallback((e, property) => {
    e.stopPropagation();
    navigate('/chat', { state: { property: property.id } });
  }, [navigate]);

  const handleShareClick = useCallback(async (e, property) => {
    e.stopPropagation();
    const url = `${window.location.origin}/lease-description/${property.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setAlertMessage('Property link copied to clipboard!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch {
      setAlertMessage('Failed to copy property link');
      setShowFailure(true);
      setTimeout(() => setShowFailure(false), 3000);
    }
  }, []);

  const handleLocationClick = useCallback((e, property) => {
    e.stopPropagation();
    setLocationLoading(prev => ({ ...prev, [property.id]: true }));
    const fallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.location)}`;
    const open = (url) => {
      setLocationLoading(prev => ({ ...prev, [property.id]: false }));
      window.open(url, '_blank', 'noopener,noreferrer');
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const dest = property.lat && property.long
            ? `https://www.google.com/maps/dir/?api=1&origin=${coords.latitude},${coords.longitude}&destination=${property.lat},${property.long}&travelmode=driving`
            : fallback;
          open(dest);
        },
        () => open(fallback),
        { timeout: 10000 }
      );
    } else { open(fallback); }
  }, []);

  const handleNextImage = useCallback((propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const p = findProperty(propertyId);
      if (!p) return prev;
      return { ...prev, [propertyId]: (prev[propertyId] + 1) % p.images.length };
    });
  }, [findProperty]);

  const handlePrevImage = useCallback((propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const p = findProperty(propertyId);
      if (!p) return prev;
      return { ...prev, [propertyId]: (prev[propertyId] - 1 + p.images.length) % p.images.length };
    });
  }, [findProperty]);

  // ── Card renderer (mirrors PropertyCard UI) ───────────────────────────────────
  const renderCard = useCallback((property) => {
    const imgIdx = currentImageIndex[property.id] || 0;

    return (
      <Card
        key={property.id}
        sx={{
          mb: 1, mx: 2, borderRadius: 3, boxShadow: 2,  // ✅ matched PropertyCard (mb: 1)
          display: 'flex',
          height: { xs: 140, sm: 170 },
          transition: 'transform 0.2s ease-in-out',
          '&:hover': { transform: 'scale(1.015)', boxShadow: 4 },
        }}
        onClick={(e) => {
          if (e.target.closest('button') || e.target.closest('svg')) return;
          navigate(`/lease-description/${property.id}`, {
            state: { propertyId: property.id, property: property.propertyData },
          });
        }}
      >
        {/* ── LEFT: Image 40% ─────────────────────────────────────────────── */}
        <Box sx={{
          position: 'relative',
          width: '40%',
          minWidth: '40%',
          height: '100%',
          overflow: 'hidden',
        }}>
          <CardMedia
            component="img"
            image={property.images[imgIdx]}
            alt="Property"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderTopLeftRadius: 12,
              borderBottomLeftRadius: 12,
            }}
          />

          {property.images.length > 1 && (
            <>
              <IconButton
                sx={{
                  position: 'absolute', top: '50%', left: 4,
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                  padding: '4px', width: '24px', height: '24px',
                }}
                onClick={e => handlePrevImage(property.id, e)}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton
                sx={{
                  position: 'absolute', top: '50%', right: 4,
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                  padding: '4px', width: '24px', height: '24px',
                }}
                onClick={e => handleNextImage(property.id, e)}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </>
          )}

          {property.images.length > 1 && (
            <Box sx={{
              position: 'absolute', bottom: 4, left: '50%',
              transform: 'translateX(-50%)', display: 'flex', gap: 0.5,
            }}>
              {property.images.map((_, i) => (
                <Box key={i}
                  onClick={e => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => ({ ...prev, [property.id]: i }));
                  }}
                  sx={{
                    width: 5, height: 5, borderRadius: '50%', cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    backgroundColor: imgIdx === i ? '#1976d2' : 'rgba(255,255,255,0.6)',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* ── RIGHT: Content 60% ──────────────────────────────────────────── */}
        <Box
          sx={{
            width: '60%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            height: '100%',
            p: { xs: 0.45, sm: 0.6 },
            boxSizing: 'border-box',
            justifyContent: 'space-between',
          }}
        >
          {/* TOP SECTION — Title, Location, Price */}
          <Box>
            {/* TITLE + ACTIONS */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
                mb: 0.1,
              }}
            >
              <Typography
                noWrap
                sx={{
                  flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                  color: '#2C1810', fontWeight: 700,
                  fontSize: { xs: '0.74rem', sm: '0.75rem' },
                  lineHeight: 1.20, mr: 0.3,
                }}
              >
                {property.title}
                {property.property_name ? ` - ${property.property_name}` : ''}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, flexShrink: 0 }}>
                {/* Wishlist — red heart (saved) + remove on click */}
                <IconButton
                  onClick={(e) => { e.stopPropagation(); handleRemove(property); }}
                  size="small"
                  sx={{
                    width: 25, height: 25, p: 0,
                    bgcolor: '#FFFFFF',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    '&:hover': { bgcolor: '#F5EDE4' },
                  }}
                >
                  <Favorite sx={{ fontSize: 17, color: '#D92D20' }} />
                </IconButton>

                <IconButton
                  onClick={(e) => handleShareClick(e, property)}
                  size="small"
                  sx={{
                    width: 25, height: 25, p: 0,
                    bgcolor: '#FFFFFF',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    '&:hover': { bgcolor: '#F5EDE4' },
                  }}
                >
                  <Share sx={{ fontSize: 14, color: '#8D6E63' }} />
                </IconButton>
              </Box>
            </Box>

            {/* LOCATION */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, mb: 0.05, mt: 1 }}>
              <IconButton
                onClick={e => handleLocationClick(e, property)}
                size="small" sx={{ p: 0.5 }}
                disabled={locationLoading[property.id]}
              >
                {locationLoading[property.id]
                  ? <CircularProgress size={14} />
                  : <LocationOn sx={{ color: '#B66A18', fontSize: '16px' }} />}
              </IconButton>

              <Typography
                noWrap
                sx={{
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                  color: '#5D4037',
                  fontSize: { xs: '0.68rem', sm: '0.8rem' },
                  lineHeight: 1.1, fontWeight: 500,
                }}
              >
                {property.location || 'Location not available'}
              </Typography>
            </Box>

            {/* PRICE + DATE */}
            <Box
              sx={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%', mb: 0.15, mt: 1,
              }}
            >
              <Typography
                noWrap
                sx={{
                  color: '#c98a2c', fontWeight: 700,
                  fontSize: { xs: '0.90rem', sm: '0.95rem' },
                  lineHeight: 1.15, letterSpacing: '0.3px',
                }}
              >
                ₹{Number(String(property.price || '').replace(/[^0-9]/g, '')).toLocaleString('en-IN')}
              </Typography>

              <Box
                sx={{
                  display: 'flex', alignItems: 'center', flexShrink: 0,
                  bgcolor: 'rgba(255,255,255,0.6)',
                  px: 0.4, py: 0.1, borderRadius: '4px',
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    color: '#121212',
                    fontSize: { xs: '0.60rem', sm: '0.58rem' },
                    fontWeight: 500,
                  }}
                >
                  {property.date
                    ? new Date(property.date).toLocaleDateString('en-IN', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                      })
                    : '--'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* BOTTOM SECTION — 3 stat boxes */}
          <Box
            sx={{
              display: 'flex', width: '100%', minWidth: 0,
              borderRadius: '6px', overflow: 'hidden',
              height: { xs: 35, sm: 32 },
              backgroundColor: '#FFFFFF',
              bgcolor: '#f1ce9943',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              mt: 0,
            }}
          >
            {/* FACING */}
            <Box
              sx={{
                flex: 1, minWidth: 0, py: 0.25, px: 0.2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.2, borderRight: '1px solid #E8DDD0',
              }}
            >
              <ExploreOutlined sx={{ fontSize: 13, color: '#8D6E63' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#121212', fontSize: { xs: '0.4rem', sm: '0.46rem' }, lineHeight: 2, fontWeight: 500 }}>
                  Facing
                </Typography>
                <Typography noWrap sx={{ color: '#2C1810', fontWeight: 700, fontSize: { xs: '0.45rem', sm: '0.52rem' }, lineHeight: 1.15 }}>
                  {property.facing || 'N/A'}
                </Typography>
              </Box>
            </Box>

            {/* AREA */}
            <Box
              sx={{
                flex: 1, minWidth: 0, py: 0.25, px: 0.2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.2, borderRight: '1px solid #E8DDD0',
              }}
            >
              <SquareFootOutlined sx={{ fontSize: 13, color: '#B66A18' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#121212', fontSize: { xs: '0.4rem', sm: '0.46rem' }, lineHeight: 2, fontWeight: 500 }}>
                  Area
                </Typography>
                <Typography noWrap sx={{ color: '#2C1810', fontWeight: 700, fontSize: { xs: '0.45rem', sm: '0.52rem' }, lineHeight: 1.15 }}>
                  {property.area || property.max_acres || 'N/A'}
                </Typography>
              </Box>
            </Box>

            {/* POSTED BY */}
            <Box
              sx={{
                flex: 1, minWidth: 0, py: 0.25, px: 0.2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.2,
              }}
            >
              <PersonOutlineOutlined sx={{ fontSize: 13, color: '#8D6E63' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#121212', fontSize: { xs: '0.4rem', sm: '0.46rem' }, lineHeight: 2, fontWeight: 500 }}>
                  Posted By
                </Typography>
                <Typography noWrap sx={{ color: '#2C1810', fontWeight: 700, fontSize: { xs: '0.45rem', sm: '0.52rem' }, lineHeight: 1.15 }}>
                  {property.posted_by || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  }, [
    currentImageIndex, navigate, handleRemove, handleShareClick,
    handleLocationClick, handlePrevImage, handleNextImage, locationLoading,
  ]);

  // ── Properties list ──────────────────────────────────────────────────────────
  const renderPropertiesSection = useMemo(() => {
    if (initialLoading) {
      return <Box sx={{ mt: 1 }}>{[1, 2, 3].map(i => <SkeletonCard key={i} />)}</Box>;
    }

    if (showSkeleton) {
      return (
        <Box>
          {isTyping && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 1, py: 1.5,
            }}>
              <CircularProgress size={16} sx={{ color: '#8B7355' }} />
              <Typography variant="caption" sx={{ color: '#888' }}>Searching...</Typography>
            </Box>
          )}
          <Box sx={{ mt: isTyping ? 0.5 : 1 }}>
            {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </Box>
        </Box>
      );
    }

    if (properties.length === 0) {
      return (
        <Box sx={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          alignItems: 'center', height: 200, gap: 1, px: 2,
        }}>
          <Typography variant="body2" color="text.secondary">
            {selectedCategory
              ? `No saved properties in "${selectedCategory.category}"`
              : debouncedSearch
              ? `No results for "${debouncedSearch}"`
              : 'You have no saved lease properties yet.'}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {properties.map(renderCard)}
        <ScrollSentinel onVisible={loadMore} hasMore={hasMore} loading={listLoading} />
      </>
    );
  }, [
    initialLoading, showSkeleton, isTyping, properties, selectedCategory,
    debouncedSearch, renderCard, loadMore, hasMore, listLoading,
  ]);

  // ── JSX ──────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: 'rgb(239, 231, 221)', minHeight: '120vh' }}>

      {/* Fixed header */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
        backgroundColor: 'rgb(239, 231, 221)',
      }}>
        {/* ✅ Search bar — matched PropertyCard padding */}
        <Box sx={{ px: 1, pt: 1, pb: 0.25 }}>
          <CustomSearchBar
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onPriceFilterChange={setPriceFilter}
          />
        </Box>

        {/* ✅ Title — matches PropertyCard tabs container spacing */}
        <Box sx={{ px: 2, pt: 0, pb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">Saved Lease Properties</Typography>
        </Box>

        {/* ✅ Category filter chips — matched PropertyCard spacing & styling */}
        <Box sx={{ px: 2, pt: 0, pb: 0.5 }}>
          {categoriesLoading ? (
            <ChipRowSkeleton />
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 0.6,
                overflowX: 'auto',
                flexWrap: 'nowrap',
                pb: 0.5,

                // hide scrollbar (Chrome / Safari / Edge)
                '&::-webkit-scrollbar': { display: 'none' },
                // hide scrollbar (Firefox)
                scrollbarWidth: 'none',
                // hide scrollbar (IE / old Edge)
                msOverflowStyle: 'none',
              }}
            >
              {categories.map(cat => {
                const isActive = selectedCategory?.category_id === cat.category_id;
                return (
                  <Chip
                    key={cat.category_id}
                    label={cat.category}
                    onClick={() =>
                      setSelectedCategory(prev =>
                        prev?.category_id === cat.category_id ? null : cat
                      )
                    }
                    sx={{
                      flexShrink: 0,
                      height: 24,
                      borderRadius: '20px',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      bgcolor: isActive ? '#000' : 'transparent',
                      color: isActive ? '#fff' : '#000',
                      border: '1px solid black',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: isActive ? '#333' : '#e0e0e0',
                        transform: 'scale(1.02)',
                      },
                      '& .MuiChip-label': {
                        paddingLeft: '6px',
                        paddingRight: '6px',
                        lineHeight: 1,
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* ✅ Scrollable list — matched PropertyCard top padding */}
      <Box sx={{
        pb: 8,
        pt: { xs: '105px', sm: '155px' },
      }}>
        {renderPropertiesSection}
      </Box>

      {/* Alerts */}
      {showSuccess    && <CopySuccessAlert        message={alertMessage} onClose={() => { setShowSuccess(false);    setAlertMessage(''); }} />}
      {showFailure    && <CopyFailureAlert        message={alertMessage} onClose={() => { setShowFailure(false);    setAlertMessage(''); }} />}
      {showRemoved    && <WishlistRemovedAlert    message={alertMessage} onClose={() => { setShowRemoved(false);    setAlertMessage(''); }} />}
      {showNotRemoved && <WishlistNotRemovedAlert message={alertMessage} onClose={() => { setShowNotRemoved(false); setAlertMessage(''); }} />}

      <BottomNavbar />
    </Box>
  );
};

export default LeaseSaves;